import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Modal, Form } from "react-bootstrap";
import axios from "axios";
import { API_URL } from "../../../../config";
import {
  normalizeSummary,
  normalizeCalls,
  fmtCallTime,
  fmtInstant,
  fmtPhone,
  fmtPercent,
  answeredShort,
  screenedLine,
  screenedParts,
  changeVsPrevious,
  outcomeClass,
  outcomeTitle,
  sourceTag,
  aboutText,
  outcomeOptions,
  filterCalls,
  LINES,
  ALL_LINES,
  ALL_OUTCOMES,
  EXPORT_COLUMNS,
  toExportRows,
} from "./orderDeskCallsHelpers";
import { exportRowsToExcel } from "./exportToExcel";
import "./OrderDeskCalls.css";

// "Order Desk Missed Calls" widget — how well the CSR team answers inbound calls on
// the Order Desk queue, from RingCentral. The RingCentral tie-in classifies every call
// (answered, voicemail, missed, junk, ...) and pushes the results to Nancy each hour;
// the API serves them as sent (OrderDeskCallsModule).
//
// Firm-wide metric, so dataScope is ignored. Weeks run Friday to Thursday, Mountain
// time — the tie-in's reporting week, which the Monday reports cover.
//
// The tile shows one week's missed calls (Voicemail + Missed) with answered calls,
// answer rate, what was screened out, and the change from the week before; the arrows
// step back through earlier weeks. Clicking the number lists every inbound call for
// that week, with the caller matched to a Nancy contact or property where possible.

const ABOUT_PREVIEW = 140;

const OrderDeskCalls = ({ auth: { auth } }) => {
  const token = auth && auth[0] ? auth[0].token : null;

  const [summary, setSummary] = useState(null);
  const [status, setStatus] = useState("loading");
  const [weekIndex, setWeekIndex] = useState(-1);

  const [showDetail, setShowDetail] = useState(false);
  const [calls, setCalls] = useState([]);
  const [callsStatus, setCallsStatus] = useState("idle");
  const [line, setLine] = useState("Order Desk");
  const [outcome, setOutcome] = useState(ALL_OUTCOMES);
  const [showJunk, setShowJunk] = useState(false);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    if (!token) return undefined;
    let active = true;
    setStatus("loading");
    axios
      .get(`${API_URL}/Dashboard/api/Dashboard/OrderDeskCalls`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (!active) return;
        const s = normalizeSummary(res.data);
        setSummary(s);
        setWeekIndex(s.currentIndex);
        setStatus("ok");
      })
      .catch((err) => {
        if (!active) return;
        console.warn("Could not load Order Desk calls:", err.message);
        setStatus("error");
      });
    return () => {
      active = false;
    };
  }, [token]);

  const weeks = summary ? summary.weeks : [];
  const week = weekIndex >= 0 ? weeks[weekIndex] : null;
  const previous = weekIndex > 0 ? weeks[weekIndex - 1] : null;
  const weekStart = week ? week.weekStart : null;

  // Load the selected week's calls while the drill-down is open.
  useEffect(() => {
    if (!showDetail || !token || !weekStart) return undefined;
    let active = true;
    setCallsStatus("loading");
    setExpanded({});
    axios
      .get(`${API_URL}/Dashboard/api/Dashboard/OrderDeskCallsDetail`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { weekStart: String(weekStart).slice(0, 10) },
      })
      .then((res) => {
        if (!active) return;
        setCalls(normalizeCalls(res.data));
        setCallsStatus("ok");
      })
      .catch((err) => {
        if (!active) return;
        console.warn("Could not load Order Desk call detail:", err.message);
        setCallsStatus("error");
      });
    return () => {
      active = false;
    };
  }, [showDetail, token, weekStart]);

  const outcomes = useMemo(() => outcomeOptions(calls, line, showJunk), [calls, line, showJunk]);
  // An outcome that isn't on the chosen line (or is hidden junk) would show nothing; fall back to all.
  const activeOutcome = outcome === ALL_OUTCOMES || outcomes.includes(outcome) ? outcome : ALL_OUTCOMES;
  const { rows, total, hiddenJunk } = useMemo(
    () => filterCalls(calls, { line, outcome: activeOutcome, showJunk, search }),
    [calls, line, activeOutcome, showJunk, search]
  );

  const step = (delta) => {
    const next = weekIndex + delta;
    if (next >= 0 && next < weeks.length) setWeekIndex(next);
  };
  const toggleAbout = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  const exportCalls = () =>
    exportRowsToExcel({
      columns: EXPORT_COLUMNS,
      rows: toExportRows(rows),
      fileName: `Order Desk Calls ${week ? week.label : ""}`,
      sheetName: "Order Desk Calls",
    });

  const weekBar = (
    <div className="odc-weekbar">
      <button type="button" className="odc-step" disabled={weekIndex <= 0} onClick={() => step(-1)}
        title="Previous week" aria-label="Previous week">‹</button>
      <span className="odc-week">{week ? `${week.isCurrent ? "This week · " : ""}${week.label}` : ""}</span>
      <button type="button" className="odc-step" disabled={weekIndex < 0 || weekIndex >= weeks.length - 1}
        onClick={() => step(1)} title="Next week" aria-label="Next week">›</button>
    </div>
  );

  // ------------------------------------------------------------- the circle
  // Same round button as Unshipped Jobs / Non Shipped Labor: green when no calls
  // were missed, red + pulse when any were, grey when the figures can't be shown
  // (a green zero after a failed load would read as "every call answered").
  const ready = status === "ok" && summary.hasData && !!week;
  const stateClass = !ready ? "odc-unknown" : week.missedCalls > 0 ? "odc-alert" : "odc-calm";
  const change = ready ? changeVsPrevious(week, previous) : null;

  let under;
  if (status === "loading") {
    under = null;
  } else if (status === "error") {
    under = <div className="odc-subnote">Unable to load the Order Desk calls.</div>;
  } else if (!ready) {
    under = (
      <div className="odc-subnote odc-subnote-wide">
        {"RingCentral call data hasn't reached Nancy yet. This fills in once the hourly RingCentral upload is pointed at Nancy."}
      </div>
    );
  } else {
    under = (
      <>
        {weekBar}
        <div className="odc-subnote" title={screenedLine(week)}>
          Screened out:{" "}
          {screenedParts(week).map((part, i) => (
            <span key={part} className="odc-nobreak">
              {i > 0 ? " · " : ""}
              {part}
            </span>
          ))}
        </div>
        {change && <div className={`odc-change odc-change-${change.dir}`}>{change.text}</div>}
        {summary.lastReceivedUtc && <div className="odc-foot">Updated {fmtInstant(summary.lastReceivedUtc)}</div>}
        {summary.feedProblem && <div className="odc-warning">{summary.feedProblem}</div>}
      </>
    );
  }

  return (
    <div className="odc-widget">
      <button
        type="button"
        className={`odc-button ${stateClass}`}
        onClick={() => ready && setShowDetail(true)}
        disabled={!ready}
        title={ready ? "View this week's calls" : "Order Desk calls are unavailable"}
      >
        {stateClass === "odc-alert" && (
          <span className="odc-icon" role="img" aria-label="phone">
            📞
          </span>
        )}
        {/* One line, like the other circles; the caption under the number says "missed calls". */}
        <span className="odc-label">Order Desk</span>
        <span className="odc-count">{status === "loading" ? "…" : ready ? week.missedCalls : "—"}</span>
        <span className="odc-count-caption">
          {ready && week.missedCalls === 1 ? "missed call" : "missed calls"}
        </span>
        <span className="odc-subcount">{ready ? answeredShort(week) : ""}</span>
      </button>
      {under}

      <Modal show={showDetail} onHide={() => setShowDetail(false)} size="xl" centered scrollable dialogClassName="ft-modal">
        <Modal.Header closeButton>
          <Modal.Title>Inbound calls · {week ? week.label : ""}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="odc-modal-bar">
            <button type="button" className="btn btn-sm btn-outline-secondary" disabled={weekIndex <= 0} onClick={() => step(-1)}>
              ‹ Previous week
            </button>
            {week && (
              <span className="odc-modal-summary">
                {week.orderDeskCalls} Order Desk {week.orderDeskCalls === 1 ? "call" : "calls"} · {week.answered} answered ·{" "}
                {week.missedCalls} missed
                {week.voicemail > 0 ? ` (${week.voicemail} left a voicemail)` : ""} · {fmtPercent(week.answerRate)} answer rate
              </span>
            )}
            <button type="button" className="btn btn-sm btn-outline-secondary"
              disabled={weekIndex < 0 || weekIndex >= weeks.length - 1} onClick={() => step(1)}>
              Next week ›
            </button>
            <button type="button" className="btn btn-sm btn-success odc-export"
              disabled={callsStatus !== "ok" || rows.length === 0} onClick={exportCalls}>
              Export to Excel
            </button>
          </div>
          {week && week.greetingHangups > 0 && (
            <div className="odc-context">
              {week.greetingHangups} more {week.greetingHangups === 1 ? "caller" : "callers"} hung up at the main greeting
              before reaching anyone. Choose the Main greeting line to see them.
            </div>
          )}

          <div className="odc-filters">
            <label className="odc-filter">
              <span>Line</span>
              <Form.Select size="sm" value={line} onChange={(e) => setLine(e.target.value)}>
                {LINES.concat(ALL_LINES).map((l) => (
                  <option key={l} value={l}>{l}</option>
                ))}
              </Form.Select>
            </label>
            <label className="odc-filter">
              <span>Outcome</span>
              <Form.Select size="sm" value={activeOutcome} onChange={(e) => setOutcome(e.target.value)}>
                {[ALL_OUTCOMES].concat(outcomes).map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </Form.Select>
            </label>
            <Form.Check type="switch" id="odc-show-junk" className="odc-junk" label="Show junk"
              checked={showJunk} onChange={(e) => setShowJunk(e.target.checked)} />
            <Form.Control size="sm" className="odc-search" placeholder="Search number, name, company, notes…"
              value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>

          {callsStatus === "loading" ? (
            <div className="ft-status">Loading…</div>
          ) : callsStatus === "error" ? (
            <div className="ft-status">{"Could not load this week's calls."}</div>
          ) : (
            <>
              <div className="ft-result-count">
                Showing {rows.length} of {total} {line === ALL_LINES ? "inbound" : line} {total === 1 ? "call" : "calls"}
                {hiddenJunk > 0 ? ` · ${hiddenJunk} junk ${hiddenJunk === 1 ? "call" : "calls"} hidden` : ""}
              </div>
              <div className="ft-scroll">
                <table className="table table-sm table-striped ft-table odc-table">
                  <thead>
                    <tr>
                      <th>Date / Time</th>
                      {line === ALL_LINES && <th>Line</th>}
                      <th>Caller Number</th>
                      <th>Caller Name</th>
                      <th>Company / Property</th>
                      <th>Outcome</th>
                      <th>Answered By</th>
                      <th>Voicemail</th>
                      <th>What They Called About</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length === 0 ? (
                      <tr>
                        <td colSpan={line === ALL_LINES ? 9 : 8} className="ft-empty">No calls match these filters.</td>
                      </tr>
                    ) : (
                      rows.map((c) => (
                        <tr key={c.callId}>
                          <td className="odc-nowrap">{fmtCallTime(c.startLocal)}</td>
                          {line === ALL_LINES && <td className="odc-nowrap">{c.line}</td>}
                          <td className="odc-nowrap">{fmtPhone(c.callerNumber) || "—"}</td>
                          <td>
                            {c.callerName || "—"}
                            <SourceTag source={c.callerNameSource} call={c} />
                          </td>
                          <td>
                            {c.companyOrProperty || "—"}
                            <SourceTag source={c.companySource} call={c} />
                          </td>
                          <td>
                            <span className={`odc-out ${outcomeClass(c.outcome)}`} title={outcomeTitle(c)}>{c.outcome}</span>
                          </td>
                          <td>{c.answeredBy || ""}</td>
                          <td>{c.leftVoicemail ? "Yes" : "No"}</td>
                          <td className="odc-about">
                            <About c={c} open={!!expanded[c.callId]} onToggle={() => toggleAbout(c.callId)} />
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
          <div className="odc-footnote">
            Mountain time. Missed calls are Order Desk calls nobody picked up, with or without a voicemail; junk, quick
            hang-ups (under 10 seconds) and calls after Order Desk hours are screened out. Name tags show where a name came
            from: <b>Nancy</b> matched the number to a Nancy contact or property, <b>known caller</b> is the RingCentral
            tie-in&apos;s own list, <b>from voicemail</b> was picked out of what the caller said, and <b>caller ID</b> is
            the phone carrier&apos;s.
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

// Where a caller name or company came from; Nancy matches say which record in the tooltip.
const SourceTag = ({ source, call }) => {
  const label = sourceTag(source, call);
  if (!label) return null;
  const title = source === "nancy" && call.nancyMatch ? call.nancyMatch.description : undefined;
  return (
    <span className={`odc-tag odc-tag-${source}`} title={title}>
      {label}
    </span>
  );
};

// The AI synopsis when there is one, else the voicemail transcript, trimmed until opened.
const About = ({ c, open, onToggle }) => {
  const text = aboutText(c);
  if (!text) return null;
  const long = text.length > ABOUT_PREVIEW;
  return (
    <>
      {!c.synopsis && <span className="odc-tag odc-tag-vm">voicemail</span>}
      {long && !open ? `${text.slice(0, ABOUT_PREVIEW).trimEnd()}… ` : `${text} `}
      {long && (
        <button type="button" className="odc-link" onClick={onToggle}>
          {open ? "less" : "more"}
        </button>
      )}
    </>
  );
};

const mapStateToProps = ({ auth }) => ({ auth });

export { OrderDeskCalls };

export default connect(mapStateToProps)(OrderDeskCalls);
